import type { PavilionLauncResults, PlaycastHostEdition, PlaycastWindowState } from '@playcastdotio/CoreTypes';
import type {
  PlaycastMessage,
  PlaycastMessageHostLogin,
  PlaycastMessageHostLogout,
  PlaycastMessageHostSetWindowState,
  PlaycastMessageSource,
  PlaycastMessageHostReconnectMessageCaptureClient,
  PlaycastMessageHostShowWebrtcInternals,
  PlaycastMessageHostStartUpdate,
  PlaycastMessageHostFetchAudioSources,
  PlaycastMessageHostShowGpuInternals,
  PlaycastMessageHostNotifyPlayjectorDisconnect,
  PlaycastMessageHostModeChanged,
  PlaycastMessageHostPavilionLaunchReply,
  PlaycastMessageHostScreenshotGrab,
  PlaycastMessageHostScreenshotGrabComplete,
  PlaycastMessageHostScheduledScreenshotGrab,
  PlaycastMessageHostScheduledScreenshotGrabComplete,
} from '../types';
import { formatMessage } from './_util/formatMessage';

const target = 'host';

export const setWindowState = (
  hostAuthId: string,
  state: PlaycastWindowState,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageHostSetWindowState> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageHostSetWindowState = {
    target,
    action: 'setWindowState',
    isReply: false,
    message: {
      state,
    },
  };
  return formatMessage(hostAuthId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageHostSetWindowState>;
};

export const login = (hostAuthId: string, userAuth?: string): PlaycastMessage<PlaycastMessageHostLogin> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageHostLogin = {
    target,
    action: 'login',
    isReply: false,
    message: {},
  };
  return formatMessage(hostAuthId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageHostLogin>;
};

export const logout = (hostAuthId: string, userAuth?: string): PlaycastMessage<PlaycastMessageHostLogout> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageHostLogout = {
    target,
    action: 'logout',
    isReply: false,
    message: {},
  };
  return formatMessage(hostAuthId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageHostLogout>;
};

export const reconnectMessageCaptureClient = (
  hostAuthId: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageHostReconnectMessageCaptureClient> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageHostReconnectMessageCaptureClient = {
    target,
    action: 'reconnectMessageCaptureClient',
    isReply: false,
    message: {},
  };
  return formatMessage(hostAuthId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageHostReconnectMessageCaptureClient>;
};

export const showWebrtcInternals = (hostAuthId: string, userAuth?: string): PlaycastMessage<PlaycastMessageHostShowWebrtcInternals> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageHostShowWebrtcInternals = {
    target,
    action: 'showWebrtcInternals',
    isReply: false,
    message: {},
  };
  return formatMessage(hostAuthId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageHostShowWebrtcInternals>;
};

export const showGpuInternals = (hostAuthId: string, userAuth?: string): PlaycastMessage<PlaycastMessageHostShowGpuInternals> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageHostShowGpuInternals = {
    target,
    action: 'showGpuInternals',
    isReply: false,
    message: {},
  };
  return formatMessage(hostAuthId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageHostShowGpuInternals>;
};

export const startUpdate = (hostAuthId: string, userAuth?: string): PlaycastMessage<PlaycastMessageHostStartUpdate> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageHostStartUpdate = {
    target,
    action: 'startUpdate',
    isReply: false,
    message: {},
  };
  return formatMessage(hostAuthId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageHostStartUpdate>;
};

export const fetchAudioSources = (hostAuthId: string, userAuth?: string): PlaycastMessage<PlaycastMessageHostFetchAudioSources> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageHostFetchAudioSources = {
    target,
    action: 'fetchAudioSources',
    isReply: false,
    message: {},
  };
  return formatMessage(hostAuthId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageHostFetchAudioSources>;
};

export const notifyPlayjectorDisconnected = (
  hostAuthId?: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageHostNotifyPlayjectorDisconnect> => {
  hostAuthId = hostAuthId || 'experimental';
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageHostNotifyPlayjectorDisconnect = {
    target,
    action: 'notifyPlayjectorDisconnect',
    isReply: false,
    message: {},
  };
  return formatMessage(hostAuthId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageHostNotifyPlayjectorDisconnect>;
};

export const hostModeChanged = (
  edition: PlaycastHostEdition,
  matchmakeable: boolean,
  serverLogin: boolean,
  guestAccount: boolean,
  steamCredentials: string,
  hostAuthId?: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageHostModeChanged> => {
  hostAuthId = hostAuthId || 'experimental';
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageHostModeChanged = {
    target,
    action: 'modeChanged',
    isReply: false,
    message: {
      edition,
      matchmakeable,
      serverLogin,
      guestAccount,
      steamCredentials,
    },
  };
  return formatMessage(hostAuthId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageHostModeChanged>;
};

/**
 * Create a pavilion launch reply message
 * Used to report launch success/failure back to the frontend
 *
 * @param result - The launch result (SUCCESS, FAILURE, etc.)
 * @param reason - Optional failure reason string for display in admin dashboard
 */
export const pavilionLaunchReply = (
  result: PavilionLauncResults,
  reason?: string,
): PlaycastMessage<PlaycastMessageHostPavilionLaunchReply> => {
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageHostPavilionLaunchReply = {
    target,
    action: 'pavilionLaunch',
    isReply: true,
    message: {
      result,
      reason,
    },
  };
  return formatMessage('host', messageSource, message) as PlaycastMessage<PlaycastMessageHostPavilionLaunchReply>;
};

/**
 * Create a screenshot grab IPC command (host-frontend → host-electron).
 *
 * The realtime API initiates the flow via `HOST_SCREENSHOT_GRAB_COMMAND`;
 * host-frontend's WS handler in turn dispatches this IPC message to
 * host-electron's screenshot handler, which reads `macro_latest.png` from
 * disk and PUTs it to the presigned `uploadUrl`. The reply
 * (`screenshotGrabComplete`) is correlated by `requestId`.
 */
export const screenshotGrab = (
  uploadUrl: string,
  requestId: string,
  hostAuthId?: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageHostScreenshotGrab> => {
  hostAuthId = hostAuthId || 'host';
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageHostScreenshotGrab = {
    target,
    action: 'screenshotGrab',
    isReply: false,
    message: { uploadUrl, requestId },
  };
  return formatMessage(hostAuthId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageHostScreenshotGrab>;
};

/**
 * Create a screenshot grab IPC reply (host-electron → host-frontend).
 *
 * The payload is a discriminated union so callers can't construct ambiguous
 * shapes (success without sizeBytes, or failure without an error message).
 * Host-frontend forwards the result onward as the realtime
 * `HOST_SCREENSHOT_GRAB_COMPLETE` WS message.
 */
export const screenshotGrabComplete = (
  result: PlaycastMessageHostScreenshotGrabComplete['message'],
  hostAuthId?: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageHostScreenshotGrabComplete> => {
  hostAuthId = hostAuthId || 'host';
  const messageSource: PlaycastMessageSource = 'host';
  const message = {
    target,
    action: 'screenshotGrabComplete' as const,
    isReply: true as const,
    message: result,
  } as PlaycastMessageHostScreenshotGrabComplete;
  return formatMessage(hostAuthId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageHostScreenshotGrabComplete>;
};

/**
 * Create a scheduled screenshot grab IPC command (host-frontend → host-electron).
 *
 * Same flow as `screenshotGrab` but host-electron reads from `scheduled_latest.png`
 * instead of `macro_latest.png`.
 */
export const scheduledScreenshotGrab = (
  uploadUrl: string,
  requestId: string,
  hostAuthId?: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageHostScheduledScreenshotGrab> => {
  hostAuthId = hostAuthId || 'host';
  const messageSource: PlaycastMessageSource = 'host';
  const message: PlaycastMessageHostScheduledScreenshotGrab = {
    target,
    action: 'scheduledScreenshotGrab',
    isReply: false,
    message: { uploadUrl, requestId },
  };
  return formatMessage(hostAuthId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageHostScheduledScreenshotGrab>;
};

/**
 * Create a scheduled screenshot grab IPC reply (host-electron → host-frontend).
 *
 * Host-frontend forwards the result onward as the realtime
 * `HOST_SCHEDULED_SCREENSHOT_GRAB_COMPLETE` WS message.
 */
export const scheduledScreenshotGrabComplete = (
  result: PlaycastMessageHostScheduledScreenshotGrabComplete['message'],
  hostAuthId?: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageHostScheduledScreenshotGrabComplete> => {
  hostAuthId = hostAuthId || 'host';
  const messageSource: PlaycastMessageSource = 'host';
  const message = {
    target,
    action: 'scheduledScreenshotGrabComplete' as const,
    isReply: true as const,
    message: result,
  } as PlaycastMessageHostScheduledScreenshotGrabComplete;
  return formatMessage(hostAuthId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageHostScheduledScreenshotGrabComplete>;
};
